import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectorRef
} from '@angular/core';
import { CspSafeAudioPlayer, downloadRecordingFile, getFetchAudioErrorMessage } from 'src/@core/utils/audio-playback.util';

@Component({
  selector: 'app-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss']
})
export class AudioPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() recordingUrl: string | null = null;
  @ViewChild('waveformContainer', { static: false }) waveformContainer!: ElementRef;

  playing = false;
  loadError: string | null = null;
  currentTime = 0;
  duration = 0;
  waveformData: number[] = [];
  progressPercentage = 0;
  playbackSpeed = 1;
  readonly speedOptions = [1, 2, 3];

  private player: CspSafeAudioPlayer | null = null;
  private loadTimeoutId: number | null = null;

  constructor(private cd: ChangeDetectorRef) { }

  ngOnInit() {
    this.generateWaveform();
    this.initPlayer();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['recordingUrl']) {
      this.resetPlaybackState();
    }
  }

  ngOnDestroy() {
    this.clearLoadTimeout();
    this.resetPlaybackState();
  }

  playRecording(url: string) {
    if (!this.player) {
      this.initPlayer();
    }
    if (!this.player) {
      this.setLoadError('Audio player is not ready. Please try again.');
      return;
    }

    this.clearLoadTimeout();

    if (this.player.isLoaded(url)) {
      if (this.player.paused) {
        this.player.play();
      } else {
        this.player.pause();
      }
      return;
    }

    this.loadError = null;
    this.playing = false;
    this.currentTime = 0;
    this.duration = 0;
    this.progressPercentage = 0;

    this.loadTimeoutId = window.setTimeout(() => {
      if (!this.playing && !this.duration) {
        this.setLoadError('Recording did not load. Please try again.');
      }
    }, 15000);

    this.player.playUrl(url).catch((err) => {
      this.clearLoadTimeout();
      this.playing = false;
      this.setLoadError(getFetchAudioErrorMessage(err));
      console.error('Audio playback failed:', url, err);
    });
  }

  private initPlayer() {
    if (this.player) {
      return;
    }
    this.player = new CspSafeAudioPlayer({
      onTimeUpdate: (time) => {
        this.currentTime = time;
        this.updateProgress();
        this.cd.markForCheck();
      },
      onDuration: (value) => {
        this.clearLoadTimeout();
        this.duration = value;
        this.loadError = null;
        this.cd.markForCheck();
      },
      onEnded: () => {
        this.playing = false;
        this.currentTime = 0;
        this.progressPercentage = 0;
        this.cd.markForCheck();
      },
      onError: (message) => {
        this.clearLoadTimeout();
        this.playing = false;
        this.setLoadError(message);
      },
      onPlayingChange: (isPlaying) => {
        this.clearLoadTimeout();
        this.playing = isPlaying;
        if (isPlaying) {
          this.loadError = null;
        }
        this.cd.markForCheck();
      }
    });
    this.player.setPlaybackRate(this.playbackSpeed);
  }

  setPlaybackSpeed(speed: number) {
    this.playbackSpeed = speed;
    if (this.player) {
      this.player.setPlaybackRate(speed);
    }
    this.cd.markForCheck();
  }

  private setLoadError(message: string) {
    this.loadError = message;
    this.cd.markForCheck();
  }

  private clearLoadTimeout() {
    if (this.loadTimeoutId !== null) {
      window.clearTimeout(this.loadTimeoutId);
      this.loadTimeoutId = null;
    }
  }

  private resetPlaybackState() {
    this.clearLoadTimeout();
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    this.playing = false;
    this.currentTime = 0;
    this.duration = 0;
    this.progressPercentage = 0;
    this.playbackSpeed = 1;
    this.loadError = null;
    this.cd.markForCheck();
  }

  generateWaveform() {
    const bars = 50;
    this.waveformData = Array.from({ length: bars }, (_, i) => {
      const baseHeight = 0.3 + Math.sin(i * 0.3) * 0.2;
      const variation = Math.random() * 0.4;
      return Math.min(1, Math.max(0.2, baseHeight + variation));
    });
  }

  getWaveformBarHeight(index: number): number {
    return this.waveformData[index] * 100;
  }

  isBarActive(index: number): boolean {
    const totalBars = this.waveformData.length;
    const activeBars = Math.floor((this.progressPercentage / 100) * totalBars);
    return index < activeBars;
  }

  getScrubberPosition(): number {
    return Math.max(0, Math.min(100, this.progressPercentage));
  }

  getBarX(index: number): number {
    const totalBars = this.waveformData.length;
    const spacing = 400 / (totalBars + 1);
    return (index + 1) * spacing;
  }

  getBarY1(index: number): number {
    const centerY = 24;
    const minBarLength = 20.6;
    const maxBarLength = 48;
    const barLength = minBarLength + (this.waveformData[index] * (maxBarLength - minBarLength));
    return centerY - (barLength / 2);
  }

  getBarY2(index: number): number {
    const centerY = 24;
    const minBarLength = 20.6;
    const maxBarLength = 48;
    const barLength = minBarLength + (this.waveformData[index] * (maxBarLength - minBarLength));
    return centerY + (barLength / 2);
  }

  seekWaveform(event: MouseEvent) {
    if (this.player && this.waveformContainer && this.duration > 0) {
      const rect = this.waveformContainer.nativeElement.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * this.duration;
      this.player.seek(newTime);
      this.currentTime = newTime;
      this.updateProgress();
      this.cd.markForCheck();
    }
  }

  updateProgress() {
    if (this.duration > 0) {
      this.progressPercentage = (this.currentTime / this.duration) * 100;
    }
  }

  formatAudioTime(time: number | undefined): string {
    if (!time) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return this.pad(minutes) + ':' + this.pad(seconds);
  }

  pad(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  copyRecordingLink() {
    if (this.recordingUrl) {
      navigator.clipboard.writeText(this.recordingUrl).then(() => {
        console.log('Recording link copied to clipboard');
      }).catch((err) => {
        console.error('Failed to copy link:', err);
      });
    }
  }

  downloadRecording() {
    if (this.recordingUrl) {
      downloadRecordingFile(this.recordingUrl);
    }
  }
}
